import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/primitives/dialog";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

export type LGoDialogSize = "sm" | "md" | "lg" | "xl";

const sizeClass: Record<LGoDialogSize, string> = {
  sm: "max-w-[480px]",
  md: "max-w-[640px]",
  lg: "max-w-[800px]",
  xl: "max-w-[1024px]",
};

export interface LGoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  size?: LGoDialogSize;
  /** Body becomes scrollable, header/footer pinned. Default true. */
  scrollable?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** When set, body wraps in a <form> with this onSubmit. */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  /** Disables footer interactivity, dims body, shows spinner. */
  loading?: boolean;
  className?: string;
}

export function LGoDialog({
  open,
  onOpenChange,
  title,
  description,
  size = "md",
  scrollable = true,
  children,
  footer,
  onSubmit,
  loading,
  className,
}: LGoDialogProps) {
  const body = (
    <div
      className={cn(
        "flex-1",
        scrollable && "max-h-[calc(100vh-12rem)] overflow-y-auto",
        loading && "opacity-60 pointer-events-none",
      )}
    >
      {children}
    </div>
  );

  const content = onSubmit ? (
    <form onSubmit={onSubmit} className="flex flex-col">
      {body}
      {footer && (
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          {footer}
        </div>
      )}
    </form>
  ) : (
    <div className="flex flex-col">
      {body}
      {footer && (
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          {footer}
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(sizeClass[size], "flex flex-col", className)}>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        {content}
      </DialogContent>
    </Dialog>
  );
}

export { DialogFooter as LGoDialogFooter };
